import Highcharts from "highcharts/highstock";
window.Highcharts = Highcharts;
import { Models, Types, Assets, Services, Utils, Colors, Logger } from "@sendum/core";

import { ILollipopData } from "../interfaces/ILollipopData";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = new Logger("VisibilityGraphsTimelineUtils");

export type Series = Highcharts.Series & { id?: string; sensorName: string; hasGroupedData?: any; xData?: number[] }; // TODO remove

export type TPlot = {
	startTime?: number | "max";
	mode?: string;
	no?: number;
	type?: string;
	endTime?: number | "max";
	time?: number | "max";
	leg?: string;
};

export const doorOpenMarker = `url(${Assets.Sensors.DoorOpenChart})`;
export const white = `url(${Assets.WhiteChart30})`;

export const DOOR_OPEN_ID = "doorOpen";
export const LINE_DOOR_OPEN_ID = "line-doorOpen";
const DEFAULT_DATE_TIME_LABEL_FORMAT = "%H:%M <br /> %e %b";
const hiddenYAxis = {
	min: 0,
	max: 1.1,
	startOnTick: false,
	endOnTick: false,
	gridLineWidth: 0,
	title: {
		text: null,
	},
	id: "hidden",
	labels: {
		enabled: false,
	},
	opposite: true,
} as Highcharts.AxisOptions;

export const getYAxis = (sensors: string[], selectedSensor: string | undefined, maxSensors: number, device?: Models.Device): Highcharts.AxisOptions[] => {
	const yAxis: Highcharts.AxisOptions[] = [hiddenYAxis];
	let i = 0;
	sensors.forEach((key, index) => {
		if (index < maxSensors && key !== selectedSensor) {
			yAxis.push(getTimelineYAxis("", key, i++, device));
		}
	});
	yAxis.push({
		tickAmount: 0, // TODO comment A2
		gridLineColor: readCSSColor(Colors.LIGHT_GRAY), // TODO uncomment A2
		tickPositions: [0, 1], // TODO comment A2
		max: 1,
		title: undefined,
		id: "timeline",
		showEmpty: true,
		opposite: false,
		labels: {
			enabled: false,
		},
		height: 60,
		top: (100 * maxSensors) + 40,
	});

	if (selectedSensor) {
		yAxis.push({
			gridLineColor: readCSSColor(Colors.LIGHT_GRAY),
			gridLineDashStyle: "Dash",
			id: "fullView",
			showEmpty: true,
			opposite: false,
			labels: {
				align: "right",
				y: -1,
			},
			lineWidth: 1,
			tickWidth: 1,
			title: {
				align: "high",
				offset: 0,
				text: "",
				rotation: 0,
				y: 0,
			},
			top: 1,
			height: 100 * maxSensors - 1,
		});
	}

	return yAxis;
};

export const getTimelineYAxis = (unit: string, yAxisId: string, offset = 0, device?: Models.Device): any => ({
	unit: ` ${unit} `,
	// tickAmount: 1,
	gridLineColor: readCSSColor(Colors.LIGHT_GRAY),
	title: null,
	id: yAxisId,
	showEmpty: true,
	opposite: false,
	startOnTick: false,
	endOnTick: false,
	labels: {
		enabled: false,
	},
	height: 99,
	top: (100 * offset),
	min: device?.getAttributesConfig()?.get(yAxisId)?.yMin,
	max: device?.getAttributesConfig()?.get(yAxisId)?.yMax,
	tickPositions: [device?.getAttributesConfig()?.get(yAxisId)?.yMin, device?.getAttributesConfig()?.get(yAxisId)?.yMax],
});

export const getUnit = (device: Models.Device, sensorName: string): string => {
	if (device) {
		const config = device.getAttributesConfig().get(sensorName);
		if (config) {
			return config["unit"];
		}
	}

	return "";
};

export const getSeriesName = (device: Models.Device, sensorName: string): string => {
	if (device) {
		const config = device.getAttributesConfig().get(sensorName);
		if (config) {
			return config["displayName"];
		}
	}

	return sensorName;
};

export const getYAxisId = (device: Models.Device, sensorName: string): string => {
	if (device) {
		const config = device.getAttributesConfig().get(sensorName);

		if (config) {
			return config["yAxisId"];
		}

		return "hidden";
	}

	return "";
};

export const getChartType = (device: Models.Device, sensorName?: string): Types.TChartType => {
	if (device && sensorName) {
		const config = device.getAttributesConfig().get(sensorName);
		if (config) {
			return config.chartType;
		}
	}

	return "line";
};

export const getChartColor = (device: Models.Device, sensorName?: string): string => {
	if (device && sensorName) {
		const config = device.getAttributesConfig().get(sensorName);
		if (config) {
			return readCSSColor(config["color"]).trim();
		}
	}

	return "#009684";
};

export const getSensorDataType = (device: Models.Device, sensorName: string): string => {
	if (device) {
		const config = device.getAttributesConfig().get(sensorName);
		if (config) {
			return config["type"];
		}
	}

	return "number";
};

export const getSensorBooleanValue = (device: Models.Device, sensorName: string, value: boolean): string => {
	if (device) {
		const config = device.getAttributesConfig().get(sensorName);
		if (config) {
			return value ? config["trueValue"] : config["falseValue"];
		}
	}

	return String(Number(value));
};

export const getHistoryBySensorName = (device: Models.Device, history: Map<string, Types.TProperty[]>, sensorName: string, virtualShipmentName?: string): number[][] => {
	if (!device?.id) {
		return [];
	}
	const deviceHistory = history.get(`${device.id}#${virtualShipmentName || device.shipment.id}#${sensorName}`);

	if (!deviceHistory) {
		return [];
	}

	return deviceHistory
		.map(_ => (_ && _.epoch ? [_.epoch, Number(_["value"])] : []))
		.filter(_ => _.length)
		.sort((a, b) => ((a[0] && b[0] && a[0] > b[0]) ? 1 : -1));
};

export const getDefaultYAxis = (unit: string, seriesName: string, yAxisId: string): any => ({
	unit: ` ${unit} `,
	title: {
		align: "high",
		textAlign: "left",
		text: seriesName + " " + unit,
		rotation: 0,
		x: 0,
		y: -5,
	},
	id: yAxisId,
	showEmpty: true,
	opposite: false,
	labels: {
		align: "left",
	},
});

export const readCSSColor = (name: string): string => {
	const bodyStyles = window.getComputedStyle(document.body);

	return bodyStyles.getPropertyValue(name);
};

export const getChartExtremes = (chart: Highcharts.Chart): Highcharts.ExtremesObject[] => chart.xAxis.map(x => ({ ...x.getExtremes() }));

const updateLegend = (chart: Highcharts.Chart): void => {
	const values = {};
	if (chart.yAxis) {
		chart.yAxis.forEach(y => {
			if (y.options.id) {
				values[y.options.id] = { min: y.options.min, max: y.options.max };
			}
		});

		setTimeout(() => {
			Services.EventEmitter.emit("update_legend_yaxis", values);
		}, 300);
	}
};

export const getDefaultHighchartsConfig = (showMapPopup: (time: number) => void, maxSensors: number): Highcharts.Options => ({
	title: {
		text: undefined,
	},
	exporting: {
		enabled: false,
		buttons: {
			contextButton: {
				menuItems: ["printChart", "separator", "downloadPNG"],
			},
		},
	},
	time: {
		timezoneOffset: Utils.time.getTzOffset(),
	},
	chart: {
		spacing: [0, 0, 0, 0],
		animation: false,
		type: "line",
		zoomType: "x",
		height: maxSensors * 100 + 100,
		events: {
			render() {
				updateLegend(this);
			},
			redraw() {
				updateLegend(this);
			},
			afterPrint() {
				window.dispatchEvent(new Event("resize"));
			},
		},
	},
	navigator: {
		enabled: true,
		series: [
			{
				fillOpacity: 0.05,
			},
		],
		height: 10,
		margin: 0,
		xAxis: {
			labels: {
				y: 12,
				align: "center",
			},
			tickPixelInterval: 90,
			startOnTick: true,
			dateTimeLabelFormats: {
				second: "%e %b",
				minute: "%e %b",
				hour: "%e %b",
				day: "%e %b",
				week: "%e %b",
				month: "%e %b",
				year: "%e %b",
			},
		},
	},
	scrollbar: {
		enabled: false,
	},
	plotOptions: {
		series: {
			allowPointSelect: true,
			showInLegend: true,
			point: {
				events: {
					click(event) {
						let time = this.x;
						const series = <Series>event.point.series;
						if (series.hasGroupedData) {
							const index = event.point.dataGroup?.start;
							if (index && series.xData) {
								time = series.xData[index];
							}
						}
						showMapPopup(time);
						const { xAxis } = event.point.series;
						xAxis.options.plotLines?.forEach(pL => {
							if (pL.id?.includes("PL-")) {
								xAxis.removePlotLine(pL.id);
							}
						});
						xAxis.addPlotLine({
							id: `PL-${time}`,
							value: this.x,
							width: 3,
							color: readCSSColor(Colors.STORM_GRAY),
							dashStyle: "ShortDot",
							zIndex: 1,
						});
					},
				},
			},
		},
	},
	tooltip: {
		split: true,
		shared: true,
		valueDecimals: 2,
		borderColor: readCSSColor(Colors.STORM_GRAY),
		formatter() {
			const values = {};
			if (this.points) {
			    let time = 0;
				this.points.forEach(point => {
					const sensorName = (<{ sensorName: string }>point.series.options?.custom)?.sensorName;
					if (sensorName) {
						values[sensorName] = { x: point.x, y: point.y };
						time = point.x;
					}
				});

				Services.EventEmitter.emit("update_legend", values);
				if (time) {
					// showMapPopup(time);
					Services.EventEmitter.emit("data_table", time);
				}
			}
			if (this.point){
				return this.point?.custom?.leg;
			} else {
				return false;
			}

		},
	},
	xAxis: {
		id: "mainXAxis",
		type: "datetime",
		ordinal: false,
		crosshair: true,
		showEmpty: true,
		events: {},
		minRange: Utils.time.MILLISECONDS_OF_HOUR,
		dateTimeLabelFormats: {
			second: DEFAULT_DATE_TIME_LABEL_FORMAT,
			minute: DEFAULT_DATE_TIME_LABEL_FORMAT,
			hour: DEFAULT_DATE_TIME_LABEL_FORMAT,
			day: DEFAULT_DATE_TIME_LABEL_FORMAT,
			week: "%e %b",
			month: "%e %b",
			year: "%e %b",
		},
		tickPixelInterval: 90,
		offset: -57,
		gridLineWidth: 1,
		endOnTick: true,
		startOnTick: true,
		gridLineColor: readCSSColor("--sw-lightGray-color"),
		gridLineDashStyle: "Dash",
	},
	legend: {
		symbolWidth: 9,
		symbolHeight: 14,
		enabled: false,
		maxHeight: 80,
		itemDistance: 5,
	},
	yAxis: [],
	rangeSelector: { enabled: false },
	responsive: {
		rules: [
			{
				condition: {
					maxWidth: 800,
				},
				chartOptions: {
					rangeSelector: {
						inputEnabled: false,
					},
				},
			},
		],
	},
});

export const buildTypedSeries = (device: Models.Device, selectedSensor: string | undefined, sensorName: string, data: number[][] | ILollipopData[] = []): Highcharts.SeriesOptionsType => {
	const unit = getUnit(device, sensorName);
	const seriesName = getSeriesName(device, sensorName);

	return {
		id: selectedSensor === sensorName ? "fullView" : sensorName,
		custom: {
			sensorName,
			unit,
		},
		name: unit ? `${seriesName}(${unit})` : seriesName,
		data,
		dataGrouping: {
			approximation: sensorName === DOOR_OPEN_ID ? "sum" : "average",
			enabled: getSensorDataType(device, sensorName) !== "boolean",
		},
		yAxis: selectedSensor === sensorName ? "fullView" : sensorName,
		turboThreshold: 10000,
		type: getChartType(device, sensorName),
		linkedTo: sensorName === DOOR_OPEN_ID ? LINE_DOOR_OPEN_ID : undefined,
		marker: {
			symbol: sensorName === DOOR_OPEN_ID ? doorOpenMarker : "circle",
			enabled: true,
			radius: 2,
		},
		showInLegend: false,
		color: getChartColor(device, sensorName),
		gapSize: 1680000,
		gapUnit: "value",
		tooltip: {
			headerFormat: "<span style=\"font-size: 10px\">{point.x:%Custom}</span><br/>",
			pointFormat: getSensorDataType(device, sensorName) === "boolean" ? "<span style=\"color:{point.color}; margin-right: 2px\">●<b>{series.name}: </b></span><b>{point.name}</b><br/>" : "<span style=\"color:{point.color}; margin-right: 2px\">●</span><b>{point.y}</b><br/>",
			valuePrefix: seriesName + ": ",
			valueSuffix: " " + unit,
			// shared: true,
		},
		showInNavigator: false,
	};
};

export const buildLollipoSeries = (device: Models.Device, sensorName: string): Highcharts.SeriesOptionsType => {
	const unit = getUnit(device, sensorName);
	const seriesName = getSeriesName(device, sensorName);

	return {
		id: LINE_DOOR_OPEN_ID,
		custom: {
			sensorName,
			unit,
		},
		name: seriesName,
		data: [],
		yAxis: "hidden",
		type: "line",
		marker: {
			symbol: doorOpenMarker,
			width: 9,
			height: 14,
			enabled: true,
		},
		showInNavigator: false,
	};
};

export const getTimeLinePlotBands = (shipments: (Models.Shipment | undefined)[], device: Models.Device | undefined): Highcharts.XAxisPlotBandsOptions[] =>
	getVirtualTimeLine(shipments, device).map(_ => ({
		id: `timeline${_.no || ""}`,
		color: _.type === "Node" ? "rgba(102, 178, 73, 0.1)" : "rgba(20, 79, 146, 0.1)",
		from: _.startTime as number,
		to: _.endTime as number,
		zIndex: 0,
	}));

const getTimeLine = (shipment: Models.BaseShipment | undefined, device: Models.Device | undefined): TPlot[] => {
	if (!shipment?.segments.length) {
		return [];
	}

	const plotBands: TPlot[] = [];

	shipment.segments.forEach((segment: Models.Segment) => {
		if (segment.status === "InProgress" || segment.status === "Completed") {
			let startTime = 0;
			let endTime = 0;
			segment.milestones.forEach(milestone => {
				const milestoneTime = milestone.actualEpochTime;// || milestone.scheduledEpochTime;
				if (milestoneTime) {
					if (!startTime && !endTime) {
						startTime = milestoneTime;
						endTime = milestoneTime;
					} else {
						startTime = milestoneTime < startTime ? milestoneTime : startTime;
						endTime = milestoneTime > endTime ? milestoneTime : endTime;
					}
				}
			});
			if (segment.status === "InProgress" && startTime === endTime) {
				endTime = 0;
			}
			plotBands.push({ startTime: startTime || segment.start, mode: segment.nodeMode || segment.routeMode, no: segment.segmentSequenceNo, type: segment.segmentType, endTime: endTime || segment.end, leg: segment.leg });
		}
	});

	const result: TPlot[] = [];
	plotBands.forEach((plot, index, arr) => {
		plot.endTime = plot.endTime || (arr[index + 1] ? arr[index + 1].startTime : "max");
		if (index === 0) {
			plot.startTime = plot.startTime || device?.releasedDate as number || plot.endTime;
		} else {
			plot.startTime = plot.startTime || arr[index - 1].endTime;
		}

		if (!plot.endTime || !plot.startTime) {
			return;
		}
		result.push({
			...plot,
			endTime: (plot.endTime === "max" ? Date.now() : plot.endTime),
			time: plot.endTime === "max" ? plot.startTime as number : ((plot.startTime as number) + (plot.endTime - (plot.startTime as number)) / 2),
		});
	});

	return result;
};

const getVirtualTimeLine = (shipments: (Models.BaseShipment | undefined)[], device?: Models.Device): TPlot[] => {
	const tL: TPlot[][] = [];

	for (const shipment of shipments) {
		tL.push(getTimeLine(shipment, device));
	}

	return Utils.flatten(tL) as TPlot[];
};

const builsTimeLineTitle = (plot: TPlot): string => {
	const backgroundColor = plot.type === "Node" ? "--sw-accent-color" : "--sw-brand-color-900";

	return `
			<div style="width:24px; height:24px; border-radius:5px; background-color: var(${backgroundColor})">
				<img style="content: var(--sw-${plot.mode?.toLowerCase() || "nono"}-svg); width:14px; height:14px; margin:5px"/>
			</div>
	`;
};

export const buildTimelineSeries = (shipments: (Models.Shipment | undefined)[], device: Models.Device | undefined): Highcharts.SeriesFlagsOptions => {
	const data: Highcharts.PointOptionsObject[] = getVirtualTimeLine(shipments, device)
		.map(_ => ({
			x: _.time as number,
			text: "",
			title: builsTimeLineTitle(_),
			shape: white,
			custom: {
				mode: _.mode,
				type: _.type,
				leg: _.leg ||"",
			},
		}));

	return {
		id: "timeline",
		name: "",
		useHTML: true,
		dataLabels: {
			useHTML: true,
		},
		data,
		y: 10,
		title: "",
		yAxis: "timeline",
		type: "flags",
		showInLegend: false,
		tooltip: {
			headerFormat: "<span style=\"font-size: 10px\">{point.x:%Custom}</span><br/>",
			pointFormat: "<span style=\"color:{point.color}; margin-right: 2px\">●</span><b>{point.y}</b><br/>",
			valuePrefix: " ",
			valueSuffix: " ",
		},
		showInNavigator: false,
	};
};
